// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { NOT_PROVIDED, vatRegime } from '../../utils/formatFacturation';
import { Champ } from './commun';

/**
 * Onglet « Canaux et émetteur » : coordonnées de l'émetteur lues dans
 * l'environnement du serveur, et état de chaque canal de transmission.
 */
export default function ChannelsTab({ canaux: channels, canauxRetires: removedChannels, emetteur: issuer }) {
  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Émetteur des factures</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ces valeurs sont lues dans l&apos;environnement du serveur, jamais écrites en
          dur : une identité inventée sur une facture est une fausse mention. Elles se
          renseignent dans le fichier .env du serveur, pas depuis cet écran.
        </Typography>
        <Grid container spacing={2}>
          <Champ label="Raison sociale" xs={4}>{issuer?.legal_name || NOT_PROVIDED}</Champ>
          <Champ label="Forme juridique" xs={4}>{issuer?.legal_form || NOT_PROVIDED}</Champ>
          <Champ label="SIRET (VIGEOS_SIRET)" xs={4}>
            {issuer?.siret || <Typography component="span" variant="body2" color="error">{NOT_PROVIDED}, l&apos;émission est bloquée</Typography>}
          </Champ>
          {vatRegime(issuer).assujetti ? (
            <Champ label="TVA intracommunautaire (régime assujetti)" xs={4}>{issuer?.vat_number || `${NOT_PROVIDED}, imprimé « NON RENSEIGNÉ » sur un document qui porte de la TVA`}</Champ>
          ) : (
            <Champ label="TVA (VIGEOS_TVA_REGIME)" xs={4}>
              Franchise en base : {vatRegime(issuer).mention}
              {!vatRegime(issuer).declare && (
                <Typography component="span" variant="caption" color="text.secondary" display="block">
                  Régime non servi par l&apos;API : franchise supposée.
                </Typography>
              )}
            </Champ>
          )}
          <Champ label="Adresse" xs={4}>{issuer?.address || NOT_PROVIDED}</Champ>
          {/* IBAN tronqué, y compris ici : l'API ne le rend jamais entier, il
              ne figure que sur le document remis au client. Identifier le
              compte suffit, et une capture de console ne doit pas porter de
              RIB. */}
          <Champ label="IBAN d'encaissement (VIGEOS_IBAN)" xs={4}>
            {issuer?.iban_configure
              ? issuer.iban
              : <Typography component="span" variant="body2" color="error">{NOT_PROVIDED}, l&apos;émission est bloquée</Typography>}
          </Champ>
          <Champ label="BIC (VIGEOS_BIC)" xs={4}>
            {issuer?.bic || <Typography component="span" variant="body2" color="error">{NOT_PROVIDED}, l&apos;émission est bloquée</Typography>}
          </Champ>
          <Champ label="Banque" xs={4}>{issuer?.banque || NOT_PROVIDED}</Champ>
          <Champ label="Contact facturation" xs={4}>{issuer?.contact || NOT_PROVIDED}</Champ>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Canaux d&apos;émission</Typography>
        {channels.map((c) => (
          <Box key={c.code} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">{c.libelle}</Typography>
              <Chip size="small" color={c.operationnel ? 'success' : 'default'}
                label={c.operationnel ? 'opérationnel' : 'non branché'} />
            </Box>
            {(c.etapes_restantes || []).length > 0 && (
              <Box component="ol" sx={{ mt: 0.5, pl: 3 }}>
                {c.etapes_restantes.map((e) => <li key={e}><Typography variant="body2">{e}</Typography></li>)}
              </Box>
            )}
            {c.operationnel && (
              <Typography variant="body2" color="text.secondary">
                Le dépôt est enregistré avec la référence rendue par le destinataire.
                C&apos;est la voie normale des premières factures.
              </Typography>
            )}
          </Box>
        ))}
        {/* Un code retiré reste listé avec son motif : une facture en base ou
            un signet peuvent encore le porter. */}
        {Object.entries(removedChannels || {}).map(([code, motif]) => (
          <Box key={code} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">{code}</Typography>
              <Chip size="small" color="default" variant="outlined" label="retiré" />
            </Box>
            <Typography variant="body2" color="text.secondary">{motif}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}
